import { AppIcon, Checkbox, ListTile, ListTileSkeleton, Loader, SearchBox, SmartListView } from '@/components';
import EmptyPlaceholder from '@/components/EmptyPlaceholder';
import { IconEmptyPlaceholderChat, IconSend, GroupAvatarIcon } from '@/assets/images';
import { handleToggleBlockClick, UserMail, Username, UserProfile, useUserDetails } from '@/features/users';
import PropTypes from 'prop-types';
import { useCallback, useState, memo, useMemo } from 'react';
import { useForwardData } from '@/features/chats/sub-features/forward-message/viewmodel/useForwardData';
import { forwardMessagesToMultipleUsers } from '@/features/chats/sub-features/forward-message/services/forwardAction';
import './ForwardMessage.scss';
import { formatUserIdToJid } from '@/features/chats/utils';
import { getUserIdFromJid, updateToast } from '@/shared/utils';
import config from '@/config';
import { useLanguage } from '@/localization/useLanguage';
import { useSelector } from 'react-redux';
import { useAmIParticipant, useGroupParticipants } from '@/features/chats/model/groupParticipants';
import { useConversationInviteInfo } from '@/features/chats/model/conversation/selectors';
import { ExternalBadge } from '@/features/invite/ui';

const ForwardListItem = memo(({ isSelected, onToggle, jid }) => {
   const { t } = useLanguage();
   const chatId = getUserIdFromJid(jid);
   const isGroup = jid.includes('mix');

   const { isBlockedByMe, isExternal } = useUserDetails(chatId, ['isBlockedByMe', 'isExternal']);
   const isNotParticipant = !useAmIParticipant(chatId);
   const { inviteStatus, isReceiver, remainingMessages } = useConversationInviteInfo(jid);
   const isInviteRestricted = inviteStatus === 1 && (isReceiver || remainingMessages <= 0);

   // Common variable — only apply relevant flag per type
   const isDisabled = isGroup ? isNotParticipant : isBlockedByMe || isInviteRestricted;

   const handleOnToggle = useCallback(
      e => {
         if (isGroup && isNotParticipant) return updateToast('not_a_participant', t('toast.NOT_A_PARTICIPANT'));
         if (!isGroup && isBlockedByMe) return handleToggleBlockClick({ e, jid });
         if (!isGroup && isInviteRestricted) {
            return updateToast('invite_limit_reached', t('invite.MESSAGE_LIMIT_REACHED'));
         }
         onToggle(jid);
      },
      [onToggle, jid, isBlockedByMe, isGroup, isNotParticipant, isInviteRestricted, t],
   );

   const { participants } = useGroupParticipants(chatId);
   const userInfo = useSelector(state => state.userInfoData?.users);

   const subTitleText = useMemo(() => {
      if (!participants?.length) return 'Group member';
      return participants.map(p => userInfo?.[p.userId]?.nickName || userInfo?.[p.userId]?.name || p.userId).join(', ');
   }, [participants, userInfo]);

   return (
      <ListTile
         title={
            <div className="ct-flex ct-aiCenter ct-gap4">
               <Username userId={chatId} className="chat-name" />
               {!isGroup && <ExternalBadge isExternalUser={isExternal} className="external-badge--compact" />}
            </div>
         }
         subTitle={
            isGroup ? (
               <span className="user-user-email ct-ellipsis">{subTitleText}</span>
            ) : (
               <UserMail userId={chatId} className="user-user-email ct-ellipsis" />
            )
         }
         avatar={
            <UserProfile
               userId={chatId}
               className="chat-avatar chat-avatar-fallback ct-flexCenter"
               defaultIcon={isGroup ? <GroupAvatarIcon /> : null}
            />
         }
         trailing={<Checkbox checked={isSelected} onChange={handleOnToggle} />}
         onClick={handleOnToggle}
         className={`forward-list-item ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
      />
   );
});

ForwardListItem.displayName = 'ForwardListItem';

ForwardListItem.propTypes = {
   isSelected: PropTypes.bool,
   onToggle: PropTypes.func,
   jid: PropTypes.string,
};

const ForwardMessagePopup = ({ activeChatJid }) => {
   const { t } = useLanguage();
   const [loading, setLoading] = useState(false);
   const [selectedJids, setSelectedJids] = useState(new Set());
   const { sections, loadMoreContacts, hasMoreContacts, searchText, handleSearch } = useForwardData();

   const handleToggleSelection = useCallback(
      jid => {
         setSelectedJids(prev => {
            const newSelected = new Set(prev);
            if (newSelected.has(jid)) {
               newSelected.delete(jid);
            } else {
               if (newSelected.size >= config.maximumAllowedUsersToForward) {
                  const message = t('toast.FORWARD_LIMIT_REACHED', {
                     count: config.maximumAllowedUsersToForward,
                  });
                  const toastId = 'forward_limit_reached';
                  updateToast(toastId, message, { autoClose: 3000 });
                  return prev;
               }
               newSelected.add(jid);
            }
            return newSelected;
         });
      },
      [t],
   );

   const renderItem = useCallback(
      ({ item }) => {
         const id = item.userId;
         const jid = formatUserIdToJid(id, item.chatType || '');
         return <ForwardListItem isSelected={selectedJids.has(jid)} onToggle={handleToggleSelection} jid={jid} />;
      },
      [selectedJids, handleToggleSelection],
   );

   const renderSectionHeader = useCallback(
      ({ section: { title } }) => <div className="forward-section-header">{title}</div>,
      [],
   );

   const renderLoaderView = (count = 1) =>
      Array.from({ length: count }).map((_, i) => <ListTileSkeleton key={`skele-fwd-${i * 2 + 1}`} />);

   const renderEmptyComponent = () => {
      if (loading) return null;
      const isSearchActive = !!searchText;

      return (
         <EmptyPlaceholder
            icon={isSearchActive ? '' : <IconEmptyPlaceholderChat />}
            title={isSearchActive ? t('chat.NO_SEARCH_RESULTS') : t('chat.EMPTY_TITLE')}
            description={isSearchActive ? '' : t('chat.EMPTY_DESCRIPTION')}
            className="forward-empty-state"
            actionbutton={false}
         />
      );
   };

   const handleForward = useCallback(async () => {
      setLoading(true);
      const toJids = Array.from(selectedJids);
      await forwardMessagesToMultipleUsers(activeChatJid, toJids, true);
      setLoading(false);
   }, [selectedJids, activeChatJid]);

   return (
      <div className="forward-message-popup ct-flex ct-fcol">
         <div className="forward-search-box">
            <SearchBox placeholder={t('common.SEARCH')} onChange={handleSearch} value={searchText} autoFocus />
         </div>

         <div className="ct-flex1 ct-flex ct-fcol">
            <SmartListView
               id="forward-list"
               sections={sections}
               renderItem={renderItem}
               renderSectionHeader={renderSectionHeader}
               keyExtractor={item => item.jid || item.userJid || item.userId}
               itemHeight={72}
               onLoadBottom={loadMoreContacts}
               hasMoreBottom={hasMoreContacts}
               LoadingBottomComponent={loading ? renderLoaderView(6) : null}
               ListEmptyComponent={renderEmptyComponent()}
            />
         </div>

         {selectedJids.size > 0 && (
            <div className="forward-action-footer ct-flex ct-jcEnd">
               <button
                  className="forward-send-btn ct-flex ct-aiCenter ct-jcCenter"
                  onClick={handleForward}
                  disabled={loading}
               >
                  {loading ? <Loader size="sm" variant="primary" /> : <AppIcon name="send" icon={<IconSend />} />}
               </button>
            </div>
         )}
      </div>
   );
};

ForwardMessagePopup.propTypes = {
   activeChatJid: PropTypes.string,
};

export default ForwardMessagePopup;
